import { useQuery } from "@apollo/client";
import ObjectHeading, { type MetaData } from "@/components/ObjectHeading";
import { ProfileSkeleton } from "@/components/Skeleton";
import { isSameDay, toFormat } from "@/lib/dateHelpers";
import { renderEventStatus } from "@/pages/events/_utils";
import { EVENT_OBJECT_HEADING_QUERY } from "./queries";

export type EventObjectHeadingWrapperProps = {
  eventSlug: string;
  variant?: "complete" | "condensed" | "landing-page";
  avatarSize?: "small" | "medium" | "large" | "xlarge";
  showMinimalMetadata?: boolean;
};

export const EventObjectHeadingWrapper = ({
  eventSlug,
  variant = "condensed",
  avatarSize = "medium",
  showMinimalMetadata = true
}: EventObjectHeadingWrapperProps) => {
  const { data, loading, error } = useQuery(EVENT_OBJECT_HEADING_QUERY, {
    variables: { slug: eventSlug },
    errorPolicy: "all",
    skip: !eventSlug
  });

  if (loading) return <ProfileSkeleton />;
  if (error || !data?.event) return null;

  const { event } = data;

  const dateText = isSameDay(event.startsAt, event.endsAt, event.timezone)
    ? toFormat(event.startsAt) || ""
    : `${toFormat(event.startsAt)} - ${toFormat(event.endsAt)}`;

  const metaData: MetaData = showMinimalMetadata
    ? [
        ...(event.facility?.name ? [{ text: event.facility.name, label: "Facility" }] : []),
        ...(dateText ? [{ text: dateText, label: "Date" }] : [])
      ]
    : [
        ...(event.eventCode ? [{ text: event.eventCode, label: "Event ID" }] : []),
        ...(event.facility?.name ? [{ text: event.facility.name, label: "Facility" }] : []),
        ...(dateText ? [{ text: dateText, label: "Date" }] : [])
      ];

  return (
    <ObjectHeading
      title={event.title}
      iconName="today"
      metaData={metaData}
      isDetailsView
      variant={variant}
      avatarSize={avatarSize}
      tag={renderEventStatus(event.status)}
    />
  );
};
